// RPT1-F4 (docs/build-briefs/RPT1-F4-my-tips-on-the-floor.md; DEC-285, DEC-268,
// DEC-281): the pure, DOM-free half of the floor's My Tips sheet
// (ui/src/myTips.ts), kept apart so a plain node:test file can import it
// (test/ui-my-tips-range.test.ts): the four range chips, the "Last shift"
// look-back, the gap fill, and the ONE rule that matters most here.
//
// THE TRAP. GET /reports/tips-by-employee answers a caller who holds
// report.tips_by_employee.view (a manager, an admin) with everyone:true, every
// employee's row, and a top-level totalCents, dayTotals and bestDay for the
// WHOLE LOCATION. So My Tips never reads those three. It finds the signed-in user's
// own row by user id (ownTipsRow) and builds everything from that row's byDay.
// No row means nothing earned, never somebody else's figure.
//
// Dates are YYYY-MM-DD labels with UTC-noon arithmetic (addDays and
// datesBetween from reportFormat.ts, never copied), so a daylight saving change
// cannot shift a day. "Today" is the server's business date and is passed in;
// nothing here reads the browser clock to choose a date.

import { addDays, datesBetween } from "./reportFormat.ts";

export type MyTipsChip = "today" | "shift" | "week" | "lastweek";

export const MY_TIPS_CHIPS: ReadonlyArray<{ id: MyTipsChip; label: string }> = [
  { id: "today", label: "Today" },
  { id: "shift", label: "Last shift" },
  { id: "week", label: "This week" },
  { id: "lastweek", label: "Last week" },
];

/** How far back Last shift looks for an earlier night with a tip. */
export const LAST_SHIFT_LOOKBACK_DAYS = 14;

/** The slice of the tips-by-employee report this sheet is allowed to read.
 * totalCents, dayTotals and bestDay are declared only so a test can plant a
 * whole-location value there and prove it is never used. */
export interface MyTipsReportInput {
  everyone: boolean;
  employees: ReadonlyArray<{
    employeeId: string;
    displayName: string;
    totalCents: number;
    byDay: Readonly<Record<string, number>>;
  }>;
  totalCents: number;
  dayTotals: Readonly<Record<string, number>>;
  bestDay: { businessDate: string; cents: number } | null;
}

/** 0 = Sunday ... 6 = Saturday. */
function weekday(dateIso: string): number {
  const [y, m, d] = dateIso.split("-").map(Number) as [number, number, number];
  return new Date(Date.UTC(y, m - 1, d, 12)).getUTCDay();
}

/** A week starts on Monday (DEC-285). */
function mondayOnOrBefore(dateIso: string): string {
  return addDays(dateIso, -((weekday(dateIso) + 6) % 7));
}

/** The dates to ask the server for. For "shift" that is the look-back window,
 * from which the sheet then picks the night (lastShiftDate). */
export function myTipsRequestRange(chip: MyTipsChip, today: string): { from: string; to: string } {
  switch (chip) {
    case "today":
      return { from: today, to: today };
    case "week":
      return { from: mondayOnOrBefore(today), to: today };
    case "lastweek": {
      const thisMonday = mondayOnOrBefore(today);
      return { from: addDays(thisMonday, -7), to: addDays(thisMonday, -1) };
    }
    case "shift":
      return { from: addDays(today, -LAST_SHIFT_LOOKBACK_DAYS), to: addDays(today, -1) };
  }
}

/** The most recent EARLIER business night, within the look-back, on which this
 * person earned a tip. Today and zero days never count. */
export function lastShiftDate(
  byDay: Readonly<Record<string, number>>,
  today: string,
): string | undefined {
  let found: string | undefined;
  for (const [date, cents] of Object.entries(byDay)) {
    if (cents <= 0 || date >= today) continue;
    if (date < addDays(today, -LAST_SHIFT_LOOKBACK_DAYS)) continue;
    if (found === undefined || date > found) found = date;
  }
  return found;
}

/** The signed-in user's OWN row, matched by user id and nothing else (a display
 * name is not an id). */
export function ownTipsRow(
  report: Pick<MyTipsReportInput, "employees">,
  userId: string,
): MyTipsReportInput["employees"][number] | undefined {
  return report.employees.find((employee) => employee.employeeId === userId);
}

export interface MyTipsDayRow {
  date: string;
  cents: number;
  /** The night that is still running: the server's business date. */
  live: boolean;
  /** The busiest of this person's own nights in a range of two or more days. */
  best: boolean;
}

export interface MyTipsView {
  chip: MyTipsChip;
  from: string;
  to: string;
  totalCents: number;
  /** Newest night first, every day in the range present. */
  rows: MyTipsDayRow[];
  maxCents: number;
  /** The range includes the server's business date, so it is not final yet. */
  live: boolean;
  /** Last shift found no earlier night with a tip. */
  none: boolean;
  /** Nothing to list: no tip at all in a single day, or Last shift found none. */
  empty: boolean;
}

export function buildMyTipsView(params: {
  chip: MyTipsChip;
  today: string;
  userId: string;
  report: MyTipsReportInput;
}): MyTipsView {
  const { chip, today, userId, report } = params;
  const own = ownTipsRow(report, userId);
  const byDay = own?.byDay ?? {};
  let from: string;
  let to: string;
  let none = false;
  if (chip === "shift") {
    const night = lastShiftDate(byDay, today);
    if (night === undefined) {
      none = true;
      from = addDays(today, -1);
      to = from;
    } else {
      from = night;
      to = night;
    }
  } else {
    ({ from, to } = myTipsRequestRange(chip, today));
  }
  const dates = none ? [] : datesBetween(from, to);
  const cents = (date: string): number => byDay[date] ?? 0;
  const totalCents = dates.reduce((sum, date) => sum + cents(date), 0);
  const maxCents = dates.reduce((max, date) => Math.max(max, cents(date)), 0);
  let bestDate: string | undefined;
  if (dates.length >= 2 && maxCents > 0) {
    bestDate = dates.find((date) => cents(date) === maxCents);
  }
  const rows = [...dates].reverse().map((date) => ({
    date,
    cents: cents(date),
    live: date === today,
    best: date === bestDate,
  }));
  return {
    chip,
    from,
    to,
    totalCents,
    rows,
    maxCents,
    live: to === today,
    none,
    empty: none || (totalCents === 0 && dates.length === 1),
  };
}

/** The tenant's calendar date at an instant, in its own time zone. Used only
 * for the "it is past midnight" wording; it never picks a date to read. */
export function tenantCalendarDate(instantMs: number, timeZone: string): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(instantMs);
}
